<template>
  <canvas ref="canvasRef" class="word-cloud" :width="widthRef" :height="heightRef"></canvas>
</template>

<style lang="scss" scoped>
.word-cloud {
  cursor: pointer;
}
</style>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps<{
  words: WordItem[]
  width?: number
  height?: number
  selectMode?: boolean
}>()

const emit = defineEmits<{
  (e: 'click', word: string): void
}>()

interface WordItem {
  text: string
  size: number
}

const canvasRef = ref<HTMLCanvasElement>()
const widthRef = computed(() => props.width ?? props.height ?? 300)
const heightRef = computed(() => props.height ?? props.width ?? 300)

// wordcloud2 must be loaded lazily since it accesses window.setImmediate at import time
let WordCloud: any = null

onMounted(async () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error - no types available for wordcloud2
  const mod = await import('wordcloud2/src/wordcloud2.js')
  WordCloud = mod.default || mod
  console.log('WordCloud: library loaded', typeof WordCloud)
  renderCloud()
})

watch([() => props.words, widthRef, heightRef], () => {
  nextTick(() => renderCloud())
})

onBeforeUnmount(() => {
  if (canvasRef.value && WordCloud) {
    WordCloud.stop()
  }
})

function getComputedBackgroundColor(el: HTMLElement): string {
  let current: HTMLElement | null = el
  while (current) {
    const bg = getComputedStyle(current).backgroundColor
    // Skip transparent / rgba with alpha 0
    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
      return bg
    }
    current = current.parentElement
  }
  return '#ffffff'
}

function renderCloud() {
  const canvas = canvasRef.value
  if (!canvas || !WordCloud || !props.words.length) return

  const words = props.words.slice(0, 50)
  const maxSize = Math.max(...words.map(w => w.size))

  // pass raw counts; use weightFactor to scale into font sizes that fill the canvas
  const list: [string, number][] = words.map(w => [w.text, w.size])
  const minDim = Math.min(widthRef.value, heightRef.value)

  // wordcloud2 uses pixel-based collision detection — transparent bg breaks it.
  // Read the actual background color from the nearest opaque ancestor.
  const bgColor = getComputedBackgroundColor(canvas)

  console.log(`WordCloud: rendering ${list.length} words, maxSize=${maxSize}, minDim=${minDim}, bg=${bgColor}`)

  WordCloud(canvas, {
    list,
    gridSize: Math.max(4, Math.round(minDim / 30)),
    shape: 'circle',
    ellipticity: 0.6,
    weightFactor: (size: number) => Math.sqrt(size / maxSize) * minDim / 5,
    minSize: 4,
    rotateRatio: 0,
    color: '#888',
    backgroundColor: bgColor,
    fontFamily: 'sans-serif',
    click: props.selectMode ? (item: [string, number]) => {
      emit('click', item[0])
    } : undefined,
  })
}
</script>
